import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Rise, MaskReveal, Overline } from "@/components/Reveal";
import { WHOLESALE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { num: "01", title: "Consistent supply", body: "Regular delivery windows and long-standing suppliers — the same names on the tickets each week." },
  { num: "02", title: "Refrigerated fleet", body: "Modern vehicles serving restaurants, farm shops and independent butchers across the region." },
  { num: "03", title: "Full traceability", body: "Every animal tracked from arrival to dispatch. Documentation on request." },
  { num: "04", title: "Cut to specification", body: "Portioned or bulk, chilled or vacuum-packed — we work to your kitchen's spec, not ours." },
];

const initial = { name: "", email: "", phone: "", subject: "Wholesale enquiry", message: "" };

export default function Wholesale() {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(values.email)) next.email = "A valid email, please.";
    if (!values.message.trim() || values.message.trim().length < 5) next.message = "Tell us a little about your kitchen.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...values, form_type: "wholesale" });
      setSent(true);
      setValues(initial);
      toast.success("Thanks — we'll be in touch about your account.");
    } catch (err) {
      toast.error("Sorry, that didn't send. Please try again or call the office.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[color:var(--cream)]">
      <Seo
        title="Wholesale — Trade Accounts for Restaurants, Farm Shops & Butchers"
        description="Wholesale meat supply from F. Drury & Sons — a Wiltshire abattoir supplying restaurants, farm shops and independent butchers across the South West. Refrigerated fleet, full traceability, cut to specification. Open an account today."
        path="/wholesale"
        keywords="wholesale butcher Wiltshire, wholesale meat South West, restaurant meat supplier Swindon, wholesale beef Wiltshire, wholesale lamb, trade butcher account"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Wholesale", path: "/wholesale" },
        ]}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Wholesale Meat Supply",
            provider: { "@id": "https://fdruryandsons.co.uk/#localbusiness" },
            areaServed: [
              { "@type": "AdministrativeArea", "name": "Wiltshire" },
              { "@type": "AdministrativeArea", "name": "South West England" },
              { "@type": "AdministrativeArea", "name": "Swindon" },
              { "@type": "AdministrativeArea", "name": "Gloucestershire" },
              { "@type": "AdministrativeArea", "name": "Somerset" },
              { "@type": "AdministrativeArea", "name": "Oxfordshire" },
            ],
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Restaurants, Farm Shops, Independent Butchers",
            },
            url: "https://fdruryandsons.co.uk/wholesale",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Wholesale Meat Catalogue",
              itemListElement: [
                { "@type": "Offer", itemOffered: { "@type": "Product", name: "Wholesale Beef" } },
                { "@type": "Offer", itemOffered: { "@type": "Product", name: "Wholesale Lamb" } },
              ],
            },
          },
        ]}
      />
      {/* Hero */}
      <section className="pt-32 md:pt-48 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <MaskReveal><Overline>Wholesale · Trade accounts</Overline></MaskReveal>
            <h1 className="mt-6 md:mt-8 font-display tracking-tightest leading-[0.88] text-[16vw] sm:text-[12vw] md:text-[9vw] text-[color:var(--ink)]">
              <MaskReveal delay={0.1}><span>For kitchens</span></MaskReveal>
              <MaskReveal delay={0.2}><span className="italic text-[color:var(--oxblood)]">who care</span></MaskReveal>
              <MaskReveal delay={0.3}><span>where their meat comes from.</span></MaskReveal>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 md:pt-12">
            <MaskReveal delay={0.35}>
              <p className="font-body italic text-lg md:text-xl leading-snug text-[color:var(--ink-light)] mt-4 md:mt-0">
                We supply restaurants, farm shops and independent butchers across Wiltshire and the South West. To become a wholesale customer, please get in touch.
              </p>
            </MaskReveal>
          </div>
        </div>
      </section>

      {/* Photo strip */}
      <section className="pb-14 md:pb-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-4 md:gap-6">
          <Rise className="col-span-12 md:col-span-7">
            <div className="clip-frame grain aspect-[16/10]">
              <img src="https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=1600&q=80" alt="Wholesale cuts on prep bench" className="w-full h-full object-cover" />
            </div>
          </Rise>
          <Rise delay={0.05} className="col-span-12 md:col-span-5 md:pt-16">
            <div className="clip-frame grain aspect-[4/5]">
              <img src="https://images.pexels.com/photos/8314505/pexels-photo-8314505.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Countryside farmland" className="w-full h-full object-cover" />
            </div>
          </Rise>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 md:py-24 border-y border-[color:var(--hairline)] bg-[color:var(--paper)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid grid-cols-12 gap-6 mb-10 md:mb-14">
            <Rise className="col-span-12 md:col-span-4"><Overline>Why work with us</Overline></Rise>
            <Rise delay={0.1} className="col-span-12 md:col-span-8">
              <h2 className="mt-3 md:mt-0 font-display text-[10vw] sm:text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                A trade partner, <span className="italic text-[color:var(--oxblood)]">not a supplier.</span>
              </h2>
            </Rise>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8 md:gap-y-12">
            {benefits.map((b, i) => (
              <Rise key={b.num} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-3 md:gap-4 items-start border-t border-[color:var(--hairline)] pt-6 md:pt-8">
                  <div className="col-span-3">
                    <div className="chapter-number text-[14vw] sm:text-[10vw] md:text-[6vw] leading-none">{b.num}</div>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-display italic text-xl sm:text-2xl md:text-3xl text-[color:var(--ink)]">{b.title}</h3>
                    <p className="mt-2 md:mt-3 font-body text-base md:text-lg text-[color:var(--ink-light)] leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <Rise>
              <Overline>Open an account</Overline>
              <h2 className="mt-4 md:mt-6 font-display text-[10vw] sm:text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                Tell us about<br /><span className="italic text-[color:var(--oxblood)]">your kitchen.</span>
              </h2>
            </Rise>
            <Rise delay={0.1}>
              <p className="mt-4 md:mt-6 font-body text-base md:text-lg text-[color:var(--ink-light)] leading-relaxed">
                A short note on your operation — the kind of cuts you use, indicative volumes, delivery day preferences — and we'll come back with the right person to help.
              </p>
            </Rise>
            <Rise delay={0.15}>
              <div className="mt-6 md:mt-8 font-ui text-[10px] md:text-xs uppercase tracking-editorial text-[color:var(--ink-light)]">
                Prefer to phone? · <a href="tel:01793852467" className="link-underline text-[color:var(--ink)]">01793 852467</a>
              </div>
            </Rise>
          </div>

          <div className="col-span-12 md:col-span-7">
            {sent ? (
              <Rise>
                <div className="border border-[color:var(--oxblood)] p-6 md:p-10 bg-[color:var(--paper)]">
                  <div className="font-display italic text-2xl md:text-4xl text-[color:var(--oxblood)]">Enquiry received.</div>
                  <p className="mt-4 font-body text-base md:text-lg text-[color:var(--ink-light)]">
                    Thank you. A member of the wholesale team will be in touch shortly to talk through your requirements.
                  </p>
                  <button type="button" className="btn-sharp-outline mt-6 md:mt-8" onClick={() => setSent(false)}>Send another</button>
                </div>
              </Rise>
            ) : (
              <form data-testid={WHOLESALE.form} onSubmit={onSubmit} noValidate className="space-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Your name*</label>
                    <input className="input-line" data-testid={WHOLESALE.inputName} value={values.name} onChange={set("name")} placeholder="First & last" required />
                    {errors.name && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.name}</div>}
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Email*</label>
                    <input className="input-line" type="email" data-testid={WHOLESALE.inputEmail} value={values.email} onChange={set("email")} placeholder="you@kitchen.com" required />
                    {errors.email && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.email}</div>}
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Phone</label>
                    <input className="input-line" type="tel" data-testid={WHOLESALE.inputPhone} value={values.phone} onChange={set("phone")} placeholder="Optional" />
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Business / kitchen*</label>
                    <input className="input-line" data-testid={WHOLESALE.inputSubject} value={values.subject} onChange={set("subject")} placeholder="Restaurant, farm shop, butcher…" required />
                  </div>
                </div>
                <div className="pt-6">
                  <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Your requirements*</label>
                  <textarea className="input-line" rows={5} data-testid={WHOLESALE.inputMessage} value={values.message} onChange={set("message")} placeholder="Cuts, volumes, delivery preferences…" required />
                  {errors.message && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.message}</div>}
                </div>

                <div className="pt-10">
                  <button type="submit" data-testid={WHOLESALE.submit} className="btn-sharp" disabled={submitting}>
                    <span>{submitting ? "Sending…" : "Submit enquiry"}</span>
                    {!submitting && <svg width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M0 5h13M9 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" /></svg>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
